import { csrfService } from './csrfService';
import { config } from '../config';

/**
 * Attach CSRF tokens to every same-API state-changing fetch, once, globally.
 *
 * The backend's CSRF middleware was written but never mounted; mounting it
 * revealed the other half of the problem — the frontend has the plumbing
 * (csrfService, fetchWithCSRF) but only a handful of call sites use it. 237
 * raw fetch() calls across 112 files send no token, and there is no axios
 * client to add an interceptor to.
 *
 * Editing 237 call sites by hand is a large, error-prone change whose failure
 * mode is a silently broken write path. Wrapping fetch once covers all of them
 * and cannot miss one. Call sites already using fetchWithCSRF keep working:
 * the header they set is preserved, and csrfService dedupes concurrent token
 * fetches behind a single promise.
 *
 * Deliberately narrow:
 *   - only unsafe methods (POST/PUT/PATCH/DELETE) are touched
 *   - only requests to our own API, so third-party calls never see the token
 *   - an existing X-CSRF-Token header is never overwritten
 *   - a failure to obtain a token does not block the request; the server is
 *     the authority on whether one was required
 */

let installed = false;

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Resolve a fetch() input to an absolute URL string, or null if unparseable. */
const resolveUrl = (input) => {
  try {
    if (typeof input === 'string') return new URL(input, window.location.origin).href;
    if (input instanceof Request) return new URL(input.url, window.location.origin).href;
    if (input && typeof input.toString === 'function') {
      return new URL(input.toString(), window.location.origin).href;
    }
  } catch (error) {
    // Unparseable — treat as third-party and leave alone.
  }
  return null;
};

/** True when the request targets our own API rather than a third party. */
const isOwnApi = (url) => {
  if (!url) return false;

  try {
    const target = new URL(url);
    if (target.origin === window.location.origin) return true;

    const apiOrigin = config?.API_URI ? new URL(config.API_URI).origin : null;
    return apiOrigin ? target.origin === apiOrigin : false;
  } catch (error) {
    return false;
  }
};

export const installCsrfFetch = () => {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const method = (init?.method || (input instanceof Request ? input.method : 'GET') || 'GET')
      .toUpperCase();

    if (!UNSAFE_METHODS.has(method)) return originalFetch(input, init);
    if (!isOwnApi(resolveUrl(input))) return originalFetch(input, init);

    // Never clobber a token a caller set deliberately (fetchWithCSRF does).
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    if (headers.has('X-CSRF-Token')) return originalFetch(input, init);

    try {
      const token = await csrfService.getToken();
      if (token) headers.set('X-CSRF-Token', token);
    } catch (error) {
      // Proceed without one: the server decides whether it was required, and
      // failing closed here would break writes for a token-fetch hiccup.
      return originalFetch(input, init);
    }

    return originalFetch(input, { ...init, headers });
  };
};

export default installCsrfFetch;
